import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ToastMessage, type } from "@/lib/toastr";
import { cn } from "@/lib/utils";
import { forgotPassword } from "@/services/Auth.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router";
import { z } from "zod";
import Logo from "../../assets/texpress.png";
import { useState } from "react";

export const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address!" }),
});
const ForgotPassword = () => {
  const navigate = useNavigate();
  const [mail, setMail] = useState("");
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "all",
    defaultValues: {
      email: "",
    },
  });

  const mutation = useMutation({
    mutationFn: (v) => forgotPassword(v),
    mutationKey: ["forgotPassword"],
    onSuccess: (data) => {
      navigate("/reset-code", {
        state: {
          email: mail,
        },
      });
      ToastMessage(type.SUCCESS, data?.data?.message);
    },
    onError: (err: any) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setMail(data?.email);
    mutation.mutate(data);
  };

  return (
    <div className="flex flex-col items-center justify-center gap-6 min-h-svh bg-muted ">
      <div className="flex flex-col items-center gap-6">
        <Link to="/">
          <img src={Logo} className="h-10" />
        </Link>
        <Card className="w-full lg:w-[448px] p-6">
          <CardHeader className="text-center lg:w-[400px] mx-auto">
            <CardTitle className="text-2xl font-medium">
              Forgot Password
            </CardTitle>
            <CardDescription className="text-gray-70">
              Enter your email address and we will send you a link to reset your
              password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid gap-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <div
                            className={cn(
                              "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                              form.formState.errors?.email === undefined
                                ? "border-input-border"
                                : "border-destructive"
                            )}
                          >
                            <div className="flex flex-col w-full">
                              <FormLabel className="text-xs text-gray-70 font-normal">
                                Email Address
                              </FormLabel>

                              <FormControl>
                                <Input
                                  type="email"
                                  className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-normal ring-transparent focus-visible:ring-transparent focus:border-none"
                                  {...field}
                                  placeholder="Enter email address"
                                />
                              </FormControl>
                            </div>
                            <div className="w-4 h-4 text-muted-foreground">
                              <Mail className="w-5 h-5" />
                            </div>
                          </div>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <Button
                    // type="submit"
                    variant="default"
                    className="w-full bg-gradient-to-r from-primary to-primary-2 h-14 rounded-[10px] p-[10px]"
                    disabled={
                      !form.formState.isDirty ||
                      !form.formState.isValid ||
                      // form.getValues("otp") == "" ||
                      form.formState.isSubmitting ||
                      mutation.isPending
                    }
                  // onClick={() => navigate("/reset-code")}
                  >
                    {mutation.isPending && (
                      <Loader2 className="text-white animate-spin" />
                    )}
                    Submit email
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
        <div className="grid gap-4 text-sm font-medium text-center text-primary w-full">
          <Link
            to="/"
            className="flex items-center justify-center text-center border-[#DAD3DA] border-[1px] h-[56px] w-full p-[10px] rounded-[10px]"
          >
            Remember your login access? Log in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
